import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BRAND, LOGO } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Free Estimate" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="navbar">
      <div className="bg-[#060f26]/80 backdrop-blur-xl border-b border-cyan-400/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
            <img
              src={LOGO}
              alt="Your Surface My Purpose logo"
              className="w-10 h-10 rounded-lg border border-cyan-400/40 object-cover"
              data-testid="nav-logo-image"
            />
            <span className="font-display font-black italic uppercase leading-none text-lg sm:text-xl tracking-tight">
              Your Surface <span className="text-cyan-400">My Purpose</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono-brand text-xs tracking-[0.25em] uppercase transition-colors ${
                    isActive ? "text-cyan-400" : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="nav-call-button"
              className="flex items-center gap-2 bg-cyan-400 text-[#030712] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-cyan-300 transition-colors"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </nav>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-[#060f26]/95 backdrop-blur-xl border-b border-cyan-400/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display text-2xl font-extrabold uppercase italic ${isActive ? "text-cyan-400" : "text-white"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="nav-mobile-call-button"
                className="mt-2 flex items-center justify-center gap-2 bg-cyan-400 text-[#030712] font-bold px-5 py-3 rounded-full"
              >
                <Phone className="w-4 h-4" /> Call {BRAND.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
